import * as React from "react";

import { getIdToken } from "@needl-ai/common/auth/auth";
import { isMSquaredHub } from "@needl-ai/common/utils/isEnv";
import {
  getActiveWorkspaceId,
  getWorkspaceFromUrl,
} from "@needl-ai/common/workspace";

import { createContentEventHandler } from "./eventHandler";

import type {
  AskRMChatParamsType,
  ContentEventType,
  ResponseType,
} from "./types";
import type { ToolCallLog } from "@/contexts/ChatContextTypes";

import { useSSERequest } from "@/hooks/useSSERequest";

export const useRMChat = () => {
  const [streamingAnswer, setStreamingAnswer] = React.useState("");
  const [toolCallLogs, setToolCallLogs] = React.useState<ToolCallLog[]>([]);
  const [citationGenerationStarted, setCitationGenerationStarted] =
    React.useState(false);
  const [aiMessageId, setAiMessageId] = React.useState<string | null>(null);

  const closeSSEConnectionRef = React.useRef<() => void>();

  const {
    makeSSERequest,
    isLoading,
    isStreaming,
    isError,
    errorSource,
    data,
    clearData,
    setLoading,
  } = useSSERequest<ResponseType, ContentEventType>();

  const handleContentEvent = React.useCallback((event: ContentEventType) => {
    const handler = createContentEventHandler(
      setStreamingAnswer,
      setToolCallLogs,
      setCitationGenerationStarted,
      setAiMessageId
    );
    handler(event);
  }, []);

  const askRMChat = React.useCallback(
    async ({
      prompt,
      session_id,
      tools,
      attachment_feed_id,
      report_type,
      is_async,
      voice_count,
      askneo_service_id,
    }: AskRMChatParamsType) => {
      setStreamingAnswer("");
      setToolCallLogs([]);
      setCitationGenerationStarted(false);
      setAiMessageId(null);

      const url = `${import.meta.env.VITE_API_URL}/chat`;

      const payload: Record<string, unknown> = {
        session_id,
        prompt: prompt,
      };

      if (tools) {
        payload.tools = tools;
      }

      if (attachment_feed_id) {
        payload.attachment_feed_id = attachment_feed_id;
      }

      if (report_type) {
        payload.report_type = report_type;
      }

      if (is_async) {
        payload.is_async = true;
      }

      if (voice_count) {
        payload.voice_count = voice_count;
      }

      if (askneo_service_id) {
        payload.askneo_service_id = askneo_service_id;
      }

      const token = (await getIdToken()) as string;
      const headers: Record<string, string> = {
        token,
      };

      if (isMSquaredHub()) {
        const workspaceSubdomain = getWorkspaceFromUrl();
        if (workspaceSubdomain) {
          headers["x-workspace-subdomain"] = workspaceSubdomain;
        }
      } else {
        const workspaceId = getActiveWorkspaceId();
        if (workspaceId) {
          headers["x-workspace-id"] = workspaceId;
        }
      }

      const closeSSEConnection = await makeSSERequest(
        url,
        {
          method: "POST",
          payload: JSON.stringify(payload),
          headers: headers,
        },
        "DONE", // Use DONE as the final status that stops loading
        handleContentEvent
      );

      closeSSEConnectionRef.current = closeSSEConnection;

      // Return the session ID for reference
      return session_id;
    },
    [makeSSERequest, handleContentEvent, setToolCallLogs]
  );

  const clearEventData = React.useCallback(() => {
    setStreamingAnswer("");
    setToolCallLogs([]);
    setCitationGenerationStarted(false);
    setAiMessageId(null);
    clearData();
    if (closeSSEConnectionRef.current) {
      closeSSEConnectionRef.current();
    }
  }, [clearData, setToolCallLogs]);

  return {
    isLoading,
    isStreaming,
    isError,
    errorSource,
    data,
    streamingAnswer,
    setLoading,
    aiMessageId,
    toolCallLogs,
    citationGenerationStarted,
    askRMChat,
    clearEventData,
  };
};
